import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Row,
  Segmented,
  Select,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  BarcodeOutlined,
  BoxPlotOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  EyeOutlined,
  FilterOutlined,
  InboxOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import moment from "moment";
import config from "../config";
import "./RTOReportingPage.css";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const BASKET_META = {
  good: { label: "Ready to restock", color: "#16803a", short: "Good" },
  repacking: { label: "Needs repacking", color: "#9a5b05", short: "Repack" },
  damaged: { label: "Damaged", color: "#c02835", short: "Damaged" },
  scrap: { label: "Scrap", color: "#475569", short: "Scrap" },
};

const PRESETS = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "All time", value: "all" },
];

const computePreset = (preset) => {
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  switch (preset) {
    case "today":
      return { startDate: startOfToday.toISOString(), endDate: new Date().toISOString() };
    case "yesterday": {
      const y = new Date(startOfToday);
      y.setDate(y.getDate() - 1);
      const yEnd = new Date(startOfToday);
      return { startDate: y.toISOString(), endDate: yEnd.toISOString() };
    }
    case "7d": {
      const s = new Date(startOfToday);
      s.setDate(s.getDate() - 6);
      return { startDate: s.toISOString(), endDate: new Date().toISOString() };
    }
    case "30d": {
      const s = new Date(startOfToday);
      s.setDate(s.getDate() - 29);
      return { startDate: s.toISOString(), endDate: new Date().toISOString() };
    }
    case "all":
    default:
      return {};
  }
};

const StatCard = ({ label, value, suffix, icon, gradient, loading, hint }) => (
  <div className="rto-stat-card" style={{ "--rto-stat-gradient": gradient }}>
    <div className="rto-stat-head">
      <span className="rto-stat-label">{label}</span>
      <span className="rto-stat-icon" style={{ background: gradient }}>{icon}</span>
    </div>
    <div className="rto-stat-value">
      {loading ? "—" : value}
      {suffix && <span className="rto-stat-suffix">{suffix}</span>}
    </div>
    {hint && <div className="rto-stat-foot">{hint}</div>}
  </div>
);

const BucketTag = ({ type, value }) => {
  const meta = BASKET_META[type] || { color: "#888", short: type };
  const bg = `${meta.color}1f`;
  return (
    <span className="rto-bucket-tag" style={{ "--rto-tag-color": meta.color, "--rto-tag-bg": bg }}>
      <span className="rto-bucket-dot" />
      {meta.short} {value || 0}
    </span>
  );
};

const BasketProductList = ({ items, basketKey }) => {
  if (!items || items.length === 0) {
    return <Text type="secondary">No products in this bucket.</Text>;
  }
  return (
    <Table
      size="small"
      rowKey={(r, i) => `${basketKey}-${r.id || r.sku || i}`}
      dataSource={items}
      pagination={false}
      columns={[
        { title: "Product", dataIndex: "name", render: (v) => <Text strong>{v || "—"}</Text> },
        { title: "SKU", dataIndex: "sku", render: (v) => v || "—" },
        { title: "Qty", dataIndex: "quantity", render: (v) => v ?? "—" },
        { title: "Batch", dataIndex: "batchNumber", render: (v) => v || "—" },
        { title: "Expiry", dataIndex: "expiryDate", render: (v) => (v ? moment(v).format("DD MMM YYYY") : "—") },
        {
          title: "Notes",
          dataIndex:
            basketKey === "good" ? "remarks" :
            basketKey === "repacking" ? "repackingNotes" :
            basketKey === "damaged" ? "damageNotes" : "scrapNotes",
          render: (v) => v || "—",
        },
      ]}
    />
  );
};

const RTOReportingPage = ({ darkTheme = false, isMobile = false }) => {
  const [preset, setPreset] = useState("7d");
  const [range, setRange] = useState(null);
  const [search, setSearch] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [basketFilter, setBasketFilter] = useState(null);
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const currentUserEmail = Cookies.get("userEmail") || "";

  const queryParams = useMemo(() => {
    const params = {};
    if (range && range.length === 2 && range[0] && range[1]) {
      params.startDate = range[0].startOf("day").toISOString();
      params.endDate = range[1].endOf("day").toISOString();
    } else {
      const p = computePreset(preset);
      if (p.startDate) params.startDate = p.startDate;
      if (p.endDate) params.endDate = p.endDate;
    }
    if (userFilter) params.userEmail = userFilter;
    return params;
  }, [preset, range, userFilter]);

  const listParams = useMemo(() => {
    const p = { ...queryParams };
    if (search) p.orderID = search;
    if (basketFilter) p.basket = basketFilter;
    p.limit = 100;
    return p;
  }, [queryParams, search, basketFilter]);

  const fetchSummary = async () => {
    setLoadingSummary(true);
    try {
      const res = await axios.get(`${config.apiBaseUrl}/rto/inbounds/summary`, {
        params: queryParams,
      });
      setSummary(res.data);
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to load RTO summary");
    } finally {
      setLoadingSummary(false);
    }
  };

  const fetchRecords = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${config.apiBaseUrl}/rto/inbounds`, {
        params: listParams,
      });
      setRecords(res.data?.data || []);
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to load RTO records");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { fetchSummary(); }, [queryParams]);
  useEffect(() => { fetchRecords(); }, [listParams]);

  const totals = summary?.totals || {};
  const today = summary?.today || {};
  const byUser = summary?.byUser || [];
  const byBasket = summary?.byBasket || [];

  const statCards = [
    { key: "todayInspections", label: "RTOs received today", value: today.totalInspections || 0, suffix: "orders", icon: <InboxOutlined />, gradient: "linear-gradient(135deg, #2563eb, #4f46e5)", hint: <span><ClockCircleOutlined /> Updated just now</span> },
    { key: "todayProducts", label: "Products inspected today", value: today.totalProducts || 0, suffix: "items", icon: <BoxPlotOutlined />, gradient: "linear-gradient(135deg, #0891b2, #06b6d4)" },
    { key: "todayScrap", label: "Scrapped today", value: today.scrap || 0, suffix: "items", icon: <DeleteOutlined />, gradient: "linear-gradient(135deg, #475569, #64748b)" },
    { key: "todayDamaged", label: "Damaged today", value: today.damaged || 0, suffix: "items", icon: <ExclamationCircleOutlined />, gradient: "linear-gradient(135deg, #c02835, #ef4444)" },
      
  ];

  const userOptions = useMemo(() => {
    const set = new Set();
    byUser.forEach((u) => {
      if (u.email) set.add(u.email);
      if (u.name) set.add(u.name);
    });
    return Array.from(set).map((v) => ({ label: v, value: v }));
  }, [byUser]);

  const exportCsv = () => {
    if (!records.length) {
      message.info("No records to export");
      return;
    }
    const header = ["Order ID", "Customer", "AWB", "Courier", "Good", "Repacking", "Damaged", "Scrap", "Total", "Received At", "Received By"];
    const rows = records.map((r) => [
      r.orderID, r.customer?.name || "", r.shipment?.awb_code || "", r.shipment?.courier_name || "",
      r.counts?.good || 0, r.counts?.repacking || 0, r.counts?.damaged || 0, r.counts?.scrap || 0,
      r.totalProducts || 0,
      r.receivedAt ? moment(r.receivedAt).format("YYYY-MM-DD HH:mm") : "",
      r.receivedBy || "",
    ]);
    const csv = [header, ...rows]
      .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `rto-report-${moment().format("YYYYMMDD-HHmm")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openRecord = (record) => { setSelected(record); setDrawerOpen(true); };

  const filterMyRecords = () => {
    if (!currentUserEmail) {
      message.warning("No dashboard email cookie found for this user.");
      return;
    }
    setUserFilter(currentUserEmail);
  };

  const recordColumns = [
    { title: "Order ID", dataIndex: "orderID", key: "orderID", render: (v) => <Text strong>{v}</Text> },
    { title: "Customer", dataIndex: ["customer", "name"], key: "customer", render: (v) => v || "—" },
    {
      title: "AWB / Courier", key: "shipment",
      render: (_, r) => (
        <span>
          <BarcodeOutlined /> {r.shipment?.awb_code || "N/A"}
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>{r.shipment?.courier_name || "N/A"}</Text>
        </span>
      ),
    },
    {
      title: "Buckets", key: "buckets",
      render: (_, r) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          <BucketTag type="good" value={r.counts?.good} />
          <BucketTag type="repacking" value={r.counts?.repacking} />
          <BucketTag type="damaged" value={r.counts?.damaged} />
          <BucketTag type="scrap" value={r.counts?.scrap} />
        </div>
      ),
    },
    {
      title: "Received by", dataIndex: "receivedBy", key: "receivedBy",
      render: (v, r) => (
        <span>
          <UserOutlined /> {v || "Unknown"}
          {r.receivedByEmail && (<><br /><Text type="secondary" style={{ fontSize: 11 }}>{r.receivedByEmail}</Text></>)}
        </span>
      ),
    },
    { title: "Received at", dataIndex: "receivedAt", key: "receivedAt", render: (v) => (v ? moment(v).format("DD MMM YYYY HH:mm") : "—") },
    {
      title: "", key: "action", width: 80,
      render: (_, r) => (
        <Button type="primary" size="small" icon={<EyeOutlined />} onClick={() => openRecord(r)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div className={`rto-reporting-container ${darkTheme ? 'dark-theme' : ''}`}>
      <div className="rto-reporting-hero">
        <div className="rto-reporting-hero-left">
          <span className="rto-reporting-hero-eyebrow">Returns reporting</span>
          <Title level={2} className="rto-reporting-hero-title">RTO Reporting</Title>         
        </div>
        <div className="rto-reporting-hero-actions">
          <Button icon={<ReloadOutlined />} onClick={() => { fetchSummary(); fetchRecords(); }}>Refresh</Button>
          <Button type="primary" icon={<ExportOutlined />} onClick={exportCsv}>Export CSV</Button>
        </div>
      </div>

      <div className="rto-stat-grid">
        {statCards.map((s) => (
          <StatCard key={s.key} label={s.label} value={s.value} suffix={s.suffix} icon={s.icon} gradient={s.gradient} loading={loadingSummary} hint={s.hint} />
        ))}
      </div>

      <div className="rto-reporting-shell">
        <div className="rto-reporting-filters">
          <Segmented value={preset} onChange={(v) => { setPreset(v); setRange(null); }} options={PRESETS} />
          <RangePicker value={range} onChange={(values) => setRange(values)} allowClear style={{ minWidth: 240 }} />
          <Input className="rto-filter-search" prefix={<SearchOutlined />} placeholder="Filter by order ID" allowClear value={search} onChange={(e) => setSearch(e.target.value)} />          
          <Select allowClear placeholder="Bucket" value={basketFilter} onChange={(v) => setBasketFilter(v || null)} options={Object.entries(BASKET_META).map(([k, m]) => ({ label: m.label, value: k }))} style={{ minWidth: 180 }} />          
        </div>

        <div className="rto-reporting-tabs">
          <Tabs
            defaultActiveKey="records"
            items={[
              {
                key: "records",
                label: <span><InboxOutlined /> Inspection records</span>,
                children: (
                  <Spin spinning={loadingList}>
                    {records.length === 0 ? (
                      <div className="rto-empty-block"><Empty description="No RTO records match your filters" /></div>
                    ) : (
                      <Table className="rto-reporting-table" rowKey="_id" columns={recordColumns} dataSource={records} pagination={{ pageSize: 10, showSizeChanger: false }} scroll={{ x: 800 }} />
                    )}
                  </Spin>
                ),
              },
              {
                key: "byUser",
                label: <span><UserOutlined /> Per-user breakdown</span>,
                children: (
                  <Spin spinning={loadingSummary}>
                    {byUser.length === 0 ? (
                      <div className="rto-empty-block"><Empty description="No user activity in this range" /></div>
                    ) : (
                      <Table
                        className="rto-reporting-table"
                        rowKey={(r) => `${r.email}-${r.name}`}
                        dataSource={byUser}
                        pagination={false}
                        columns={[
                          { title: "User", key: "user", render: (_, r) => (
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <Avatar style={{ background: "linear-gradient(135deg,#2563eb,#4f46e5)" }} icon={<UserOutlined />} />
                              <span><Text strong>{r.name}</Text><br /><Text type="secondary" style={{ fontSize: 11 }}>{r.email}</Text></span>
                            </div>
                          )},
                          { title: "Inspections", dataIndex: "inspections", align: "center" },
                          { title: "Products", dataIndex: "products", align: "center" },
                          { title: "Good", dataIndex: "good", align: "center", render: (v) => <BucketTag type="good" value={v} /> },
                          { title: "Repack", dataIndex: "repacking", align: "center", render: (v) => <BucketTag type="repacking" value={v} /> },
                          { title: "Damaged", dataIndex: "damaged", align: "center", render: (v) => <BucketTag type="damaged" value={v} /> },
                          { title: "Scrap", dataIndex: "scrap", align: "center", render: (v) => <BucketTag type="scrap" value={v} /> },
                        ]}
                      />
                    )}
                  </Spin>
                ),
              },
              {
                key: "byBasket",
                label: <span><CheckCircleOutlined /> Bucket totals</span>,
                children: (
                  <Row gutter={[14, 14]}>
                    {Object.keys(BASKET_META).map((key) => {
                      const entry = byBasket.find((b) => b._id === key);
                      const meta = BASKET_META[key];
                      const gradient = key === "good" ? "linear-gradient(135deg,#16803a,#22c55e)" :
                                       key === "repacking" ? "linear-gradient(135deg,#9a5b05,#d97706)" :
                                       key === "damaged" ? "linear-gradient(135deg,#c02835,#ef4444)" :
                                                            "linear-gradient(135deg,#475569,#64748b)";
                      const icon = key === "good" ? <CheckCircleOutlined /> :
                                   key === "repacking" ? <BoxPlotOutlined /> :
                                   key === "damaged" ? <ExclamationCircleOutlined /> :
                                                        <DeleteOutlined />;
                      return (
                        <Col key={key} xs={12} md={6}>
                          <StatCard label={meta.label} value={entry?.count || 0} suffix="items" icon={icon} gradient={gradient} loading={loadingSummary} />
                        </Col>
                      );
                    })}
                  </Row>
                ),
              },
            ]}
          />
        </div>
      </div>

      <Drawer
        className="rto-reporting-drawer"
        title={selected ? `RTO #${selected.orderID}` : "RTO details"}
        open={drawerOpen}
        width={isMobile ? "100%" : 720}
        onClose={() => setDrawerOpen(false)}
      >
        {selected && (
          <div>
            <Card style={{ borderRadius: 14, marginBottom: 16 }} bodyStyle={{ padding: 0 }}>
              <Descriptions size="small" column={2} bordered>
                <Descriptions.Item label="Order ID" span={2}><Text strong>{selected.orderID}</Text></Descriptions.Item>
                <Descriptions.Item label="Customer">{selected.customer?.name || "—"}</Descriptions.Item>
                <Descriptions.Item label="Mobile">{selected.customer?.mobile || "—"}</Descriptions.Item>
                <Descriptions.Item label="Email" span={2}>{selected.customer?.email || "—"}</Descriptions.Item>
                <Descriptions.Item label="AWB">{selected.shipment?.awb_code || "—"}</Descriptions.Item>
                <Descriptions.Item label="Courier">{selected.shipment?.courier_name || "—"}</Descriptions.Item>
                <Descriptions.Item label="Total products">{selected.totalProducts || 0}</Descriptions.Item>
                <Descriptions.Item label="Buckets">
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    <BucketTag type="good" value={selected.counts?.good} />
                    <BucketTag type="repacking" value={selected.counts?.repacking} />
                    <BucketTag type="damaged" value={selected.counts?.damaged} />
                    <BucketTag type="scrap" value={selected.counts?.scrap} />
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Received at">
                  {selected.receivedAt ? moment(selected.receivedAt).format("DD MMM YYYY HH:mm:ss") : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Received by">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar size="small" icon={<UserOutlined />} style={{ background: "linear-gradient(135deg,#2563eb,#4f46e5)" }} />
                    <span>{selected.receivedBy || "—"}<br /><Text type="secondary" style={{ fontSize: 11 }}>{selected.receivedByEmail}</Text></span>
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Tabs
              defaultActiveKey="good"
              items={Object.keys(BASKET_META).map((key) => {
                const meta = BASKET_META[key];
                const items = (selected.classification || {})[key] || [];
                return {
                  key,
                  label: <span><span style={{ color: meta.color }}>●</span> {meta.short} ({items.length})</span>,
                  children: <div className="rto-drawer-products"><BasketProductList items={items} basketKey={key} /></div>,
                };
              })}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default RTOReportingPage;
